import axios, { AxiosRequestConfig } from 'axios';
import { AppConfig } from '@/config/app.config';

/**
 * GS-900 — the operator's sign-in. SES signs an agent in at `/api/auth/signin` and answers a token; every SES call then
 * carries it in `x-skyXchange-authentication` (SES reads that header; gs-gateway reads it too, so the same request works
 * through the gateway). SES refuses a player's token on `/api/director/**` (403) and gs-settlement-service decides which
 * agents may declare (its RESULT_OPERATOR / RESULT_SUPERVISOR lists), so the site only keeps a player out of the pages.
 *
 * The session lives in sessionStorage: one browser tab, gone when it closes, never shared with another site.
 */
export interface OperatorSession {
  token: string;
  username: string;
  roles: string[];
  /** Epoch ms: the token's own expiry, or 12 hours when it carries none. */
  expiresAt: number;
}

const STORAGE_KEY = 'rb-result-ui.session';
const HEADER = 'x-skyXchange-authentication';
const PLAYER_ROLES = ['ROLE_USER', 'ROLE_PLAYER'];
const DEFAULT_LIFETIME_MS = 12 * 60 * 60 * 1000;

/** What SES's sign-in answers: a token and the account, or {message, status: 'ERROR'} for a refused account. */
interface SigninAnswer {
  token?: string;
  username?: string;
  roles?: string[];
  message?: string;
}

/** The token's `exp` (seconds) as epoch ms, or null when it cannot be read. */
function expiryOf(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

class AuthService {
  private installed = false;

  public session(): OperatorSession | null {
    let session: OperatorSession | null = null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      session = raw ? JSON.parse(raw) : null;
    } catch (e) {
      session = null;
    }
    if (session && (!session.token || session.expiresAt <= Date.now())) {
      this.signOut();
      return null;
    }
    return session;
  }

  public isSignedIn(): boolean {
    return this.session() !== null;
  }

  /** Signs in and keeps the session; a refusal throws an Error with the words to show. */
  public async signIn(username: string, password: string): Promise<OperatorSession> {
    const name = (username || '').trim();
    if (!name || !password) {
      throw new Error('Enter the username and the password.');
    }
    let data: SigninAnswer;
    try {
      const res = await axios.post<SigninAnswer>(AppConfig.sesBase + AppConfig.signinPath, { username: name, password },
        { withCredentials: false });
      data = res.data;
    } catch (e) {
      const status = axios.isAxiosError(e) && e.response ? e.response.status : 0;
      throw new Error(status === 401 ? 'Wrong username or password.'
        : status === 429 ? 'Too many attempts. Wait a minute and try again.'
          : 'Sign-in failed (' + (status || 'no answer') + ').');
    }
    // SES answers a refused account (suspended, locked, wrong site) with 200 and {message, status: 'ERROR'}
    if (!data || !data.token) {
      throw new Error((data && data.message) || 'Sign-in failed.');
    }
    const roles: string[] = Array.isArray(data.roles) ? data.roles : [];
    if (roles.length === 0 || roles.some((role) => PLAYER_ROLES.indexOf(role) >= 0)) {
      throw new Error('This account cannot declare results.');
    }
    const session: OperatorSession = {
      token: data.token,
      username: data.username || name,
      roles,
      expiresAt: expiryOf(data.token) || Date.now() + DEFAULT_LIFETIME_MS,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (e) {
      throw new Error('This browser does not keep a session (sessionStorage is blocked).');
    }
    return session;
  }

  public signOut(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // nothing kept, nothing to remove
    }
  }

  /** Whether a request goes to SES (the only host that gets the token). */
  private isSesCall(config: AxiosRequestConfig): boolean {
    const url = config.url || '';
    if (AppConfig.sesBase === '') {
      return url.startsWith('/') && !url.startsWith('//');
    }
    return url.startsWith(AppConfig.sesBase + '/');
  }

  /**
   * Adds the token to every SES call, and on a 401 from SES (the session ended: signed out elsewhere, expired) sends the
   * operator back to sign in. Installed once, at start-up, and only where sign-in is required.
   */
  public install(): void {
    if (this.installed || !AppConfig.requireSignin) {
      return;
    }
    this.installed = true;
    axios.interceptors.request.use((config) => {
      const session = this.session();
      if (session && this.isSesCall(config)) {
        config.headers = Object.assign({}, config.headers, { [HEADER]: 'Bearer ' + session.token });
      }
      return config;
    });
    axios.interceptors.response.use((res) => res, (error) => {
      const res = error && error.response;
      if (res && res.status === 401 && this.isSesCall(res.config || {}) && !String((res.config || {}).url).endsWith(AppConfig.signinPath)) {
        this.signOut();
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
      return Promise.reject(error);
    });
  }
}

export const authService = new AuthService();
